import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Divider, Typography, useTheme } from '@mui/material';
import { CommentsService, type Comment, type CommentParent } from '@watchlist/shared';
import { font } from '../../utils/fontHelper';
import { getColors } from '../../tokens/designTokens';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../utils/ToastProvider';
import CommentRow from './CommentRow';
import LikedByBottomSheet from '../dialogs/LikedByBottomSheet';

const PAGE_SIZE = 20;

interface CommentsListProps {
  parent: CommentParent;
  onUserClick?: (uid: string) => void;
  /** Fires every time the live comment count changes — used by SocialPostDialog to
   *  drive the count next to the comment icon in PostActionBar, so it stays correct
   *  even before the Cloud Function commentCount increment has propagated. */
  onCountChange?: (count: number) => void;
  /** Bump this to force an immediate re-fetch (re-subscribe). Useful right after the
   *  composer adds a new comment — onSnapshot is fast on web, but native polling has
   *  a 15 s interval, so the new comment otherwise doesn't appear until the next tick. */
  refreshKey?: number;
  /** When set, the matching comment is auto-paged into view, scrolled into the viewport,
   *  and briefly flashed. Used by `comment_liked` push deep-links. */
  highlightId?: string | null;
  /** Hide the internal "Comments" heading — use when the parent already renders one. */
  hideHeading?: boolean;
  /** Override zIndex for menus/dialogs that open from within comments (use inside high-zIndex overlays). */
  popoverZIndex?: number;
}

const CommentsList: React.FC<CommentsListProps> = ({ parent, onUserClick, onCountChange, refreshKey = 0, highlightId, hideHeading = false, popoverZIndex }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getColors(isDark);
  const { user, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // User ids to show in the "Liked by" sheet — set when a comment's count is tapped.
  const [likedByIds, setLikedByIds] = useState<string[] | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  // Refs for the comment rows so we can scroll the highlighted one into view.
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // One-shot guard so we only scroll/flash on the first time the target appears.
  const highlightDoneRef = useRef<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setVisibleCount(PAGE_SIZE);

    unsubRef.current = CommentsService.subscribeToComments(parent, incoming => {
      setComments(incoming);
      setLoading(false);
    });

    return () => {
      unsubRef.current?.();
      unsubRef.current = null;
    };
  }, [parent.type, parent.id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onCountChange?.(comments.length);
  }, [comments.length, onCountChange]);

  // Reset the one-shot guard if the highlight target changes (e.g. navigating to
  // another notification before closing the dialog).
  useEffect(() => {
    highlightDoneRef.current = null;
  }, [highlightId]);

  // Auto-page the highlighted comment into view: if it sits beyond `visibleCount`,
  // bump the page size until it's rendered. Then scroll it into the viewport.
  useEffect(() => {
    if (!highlightId || comments.length === 0) return;
    if (highlightDoneRef.current === highlightId) return;
    const idx = comments.findIndex(c => c.id === highlightId);
    if (idx < 0) return;
    if (idx >= visibleCount) {
      // Round up to the next page boundary so we don't expand more than needed.
      const next = Math.ceil((idx + 1) / PAGE_SIZE) * PAGE_SIZE;
      setVisibleCount(next);
      return;
    }
    // Defer until the row is in the DOM, then scroll + mark done.
    const node = rowRefs.current.get(highlightId);
    if (!node) return;
    highlightDoneRef.current = highlightId;
    requestAnimationFrame(() => {
      try {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {
        node.scrollIntoView();
      }
    });
  }, [highlightId, comments, visibleCount]);

  const handleDelete = async (commentId: string) => {
    try {
      await CommentsService.deleteComment(commentId);
    } catch (err) {
      console.error('CommentsList: delete failed', err);
    }
  };

  const handleReport = async (commentId: string) => {
    if (!user) return;
    try {
      await CommentsService.reportComment(commentId, user.uid);
      showSuccess('Reported. Our moderators will take a look.');
    } catch (err) {
      console.error('CommentsList: report failed', err);
      showError('Could not submit report. Please try again.');
    }
  };

  const visible = comments.slice(0, visibleCount);
  const hiddenCount = comments.length - visibleCount;

  return (
    <Box>
      {!hideHeading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 15, color: colors.text.primary }}>
            Comments
          </Typography>
          {comments.length > 0 && (
            <Typography sx={{ fontFamily: font(), fontSize: 13, color: colors.text.secondary }}>
              {comments.length}
            </Typography>
          )}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : comments.length === 0 ? (
        <Typography sx={{ fontFamily: font(), fontSize: 14, color: colors.text.secondary, pb: 1 }}>
          No comments yet. Be the first!
        </Typography>
      ) : (
        <Box>
          {visible.map((comment, i) => (
            <React.Fragment key={comment.id}>
              <Box
                ref={(el: HTMLDivElement | null) => {
                  if (el) rowRefs.current.set(comment.id, el);
                  else rowRefs.current.delete(comment.id);
                }}
              >
                <CommentRow
                  comment={comment}
                  currentUserId={user?.uid}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                  onReport={handleReport}
                  onUserClick={onUserClick}
                  onLikedByClick={setLikedByIds}
                  isHighlighted={comment.id === highlightId}
                  popoverZIndex={popoverZIndex}
                />
              </Box>
              {i < visible.length - 1 && <Divider sx={{ opacity: 0.4 }} />}
            </React.Fragment>
          ))}

          {hiddenCount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <ButtonBase
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                sx={{
                  px: 2, py: 0.75, borderRadius: 2,
                  fontFamily: font(), fontSize: 13, color: colors.text.secondary,
                  '&:hover': { bgcolor: colors.ui.hover },
                }}
              >
                Show {hiddenCount} more
              </ButtonBase>
            </Box>
          )}
        </Box>
      )}

      <LikedByBottomSheet
        open={likedByIds !== null}
        onClose={() => setLikedByIds(null)}
        userIds={likedByIds ?? []}
        onUserClick={onUserClick}
        zIndex={popoverZIndex}
      />
    </Box>
  );
};

export default CommentsList;
